import React, { useEffect, useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import Header from './components/Header';
import HeroSection from './components/HeroSection';
import BenefitsSection from './components/BenefitsSection';
import HowItWorksSection from './components/HowItWorksSection';
import AboutSection from './components/AboutSection';
import FAQSection from './components/FAQSection';
import ServiceAreasSection from './components/ServiceAreasSection';
import BlogPreviewSection from './components/BlogPreviewSection';
import ContactSection from './components/ContactSection';
import Footer from './components/Footer';

// Service Pages
import LadyBirdDeedFlorida from './pages/LadyBirdDeedFlorida';
import LadyBirdDeedSarasota from './pages/LadyBirdDeedSarasota';
import LadyBirdDeedVenice from './pages/LadyBirdDeedVenice';
import LadyBirdDeedOrlando from './pages/LadyBirdDeedOrlando';
import LadyBirdDeedTampa from './pages/LadyBirdDeedTampa';
import LadyBirdDeedFortLauderdale from './pages/LadyBirdDeedFortLauderdale';

// Blog Pages
import BlogIndex from './pages/blog/BlogIndex';
import BlogWhatIsLadyBirdDeed from './pages/blog/WhatIsLadyBirdDeed';
import BlogLadyBirdVsTrust from './pages/blog/LadyBirdVsTrust';
import BlogLadyBirdCost from './pages/blog/LadyBirdCost';
import BlogLadyBirdRequirements from './pages/blog/LadyBirdRequirements';
import BlogLadyBirdProbate from './pages/blog/LadyBirdProbate';

// Landing Page (no navigation)
import LandingPage from './pages/LandingPage';

// Scroll to top on route change
function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const element = document.querySelector(hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);

  return null;
}

// Homepage component
const HomePage = () => {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in-view');
        }
      });
    }, observerOptions);

    const sections = document.querySelectorAll('.section-fade');
    sections.forEach(section => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Header scrollY={scrollY} />
      <main>
        <HeroSection />
        <BenefitsSection />
        <HowItWorksSection />
        <ServiceAreasSection />
        <AboutSection />
        <FAQSection />
        <BlogPreviewSection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
};

// Layout wrapper for pages with header/footer
const PageLayout = ({ children }) => {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Header scrollY={scrollY} />
      {children}
      <Footer />
    </div>
  );
};

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          {/* Homepage */}
          <Route path="/" element={<HomePage />} />
          
          {/* Service Pages */}
          <Route path="/lady-bird-deed-florida" element={<PageLayout><LadyBirdDeedFlorida /></PageLayout>} />
          <Route path="/lady-bird-deed-sarasota" element={<PageLayout><LadyBirdDeedSarasota /></PageLayout>} />
          <Route path="/lady-bird-deed-venice-fl" element={<PageLayout><LadyBirdDeedVenice /></PageLayout>} />
          <Route path="/lady-bird-deed-orlando-fl" element={<PageLayout><LadyBirdDeedOrlando /></PageLayout>} />
          <Route path="/lady-bird-deed-tampa-fl" element={<PageLayout><LadyBirdDeedTampa /></PageLayout>} />
          <Route path="/lady-bird-deed-fort-lauderdale" element={<PageLayout><LadyBirdDeedFortLauderdale /></PageLayout>} />
          
          {/* Blog */}
          <Route path="/blog" element={<PageLayout><BlogIndex /></PageLayout>} />
          <Route path="/blog/what-is-a-lady-bird-deed-florida" element={<PageLayout><BlogWhatIsLadyBirdDeed /></PageLayout>} />
          <Route path="/blog/lady-bird-deed-vs-living-trust-florida" element={<PageLayout><BlogLadyBirdVsTrust /></PageLayout>} />
          <Route path="/blog/how-much-does-a-lady-bird-deed-cost-florida" element={<PageLayout><BlogLadyBirdCost /></PageLayout>} />
          <Route path="/blog/lady-bird-deed-requirements-florida" element={<PageLayout><BlogLadyBirdRequirements /></PageLayout>} />
          <Route path="/blog/does-lady-bird-deed-avoid-probate-florida" element={<PageLayout><BlogLadyBirdProbate /></PageLayout>} />
          
          {/* Landing Page — No Header/Footer for paid ad traffic */}
          <Route path="/lp" element={<LandingPage />} />
        </Routes>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
